import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import "./loan-apply-form.css";
import { useParams, useNavigate } from "react-router-dom";

// --- Validation Schemas ---
const allowedEmailDomains = [
    "gmail.com", "yahoo.com", "outlook.com", "hotmail.com",
    "icloud.com", "aol.com", "protonmail.com", "mail.com",
    "yandex.com", "rediffmail.com"
];

const schemas = [
    // Step 0: Personal Info
    Yup.object().shape({
        full_name: Yup.string().min(3, "Too Short").required("Full Name required"),
        email: Yup.string()
            .email("Invalid email format")
            .required("Email required")
            .test('domain-check', 'Please use an email from a supported provider (e.g., gmail.com, yahoo.com, outlook.com)', function(value) {
                if (!value) return false;
                const domain = value.split('@')[1];
                return allowedEmailDomains.includes(domain);
            }),
        contact_no: Yup.string().matches(/^[0-9]{10}$/, "Must be exactly 10 digits").required("Mobile number required"),
        birth_date: Yup.date()
            .max(new Date(), "Date of birth cannot be in the future")
            .test('age', 'You must be at least 18 years old', function(value) {
                if (!value) return false;
                const today = new Date();
                const birthDate = new Date(value);
                const age = today.getFullYear() - birthDate.getFullYear();
                const monthDiff = today.getMonth() - birthDate.getMonth();
                if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                    return age - 1 >= 18;
                }
                return age >= 18;
            })
            .required("Date of Birth is required"),
        gender: Yup.string().required("Please select your gender"),
        // FIXED: Now accepts lowercase/uppercase but enforces the standard Indian PAN pattern
        pan_no: Yup.string().matches(/^[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}$/, "Invalid PAN (e.g., ABCDE1234F)").required("PAN is required"),
    }),
    // Step 1: Employment & Loan Details
    Yup.object().shape({
        loan_type: Yup.string().required("Please select a loan type"),
        bank_id: Yup.number().required("Please select a bank"),
        employee_type: Yup.string().required("Occupation type required"),
        monthly_income: Yup.number().positive("Must be positive").required("Income required"),
        request_amount: Yup.number().positive("Must be positive").required("Request amount required"),
        cibil_score: Yup.number().min(300, "CIBIL score must be between 300-900").max(900, "CIBIL score must be between 300-900").nullable(),
        live_loan_emi: Yup.number().min(0, "Must be positive or zero").nullable(),
    }),
    // Step 2: Address & Consent
    Yup.object().shape({
        address: Yup.string().required("Full address is required"),
        state: Yup.string().required("State is required"),
        city: Yup.string().required("City is required"),
        pin_code: Yup.string().matches(/^[0-9]{6}$/, "Must be exactly 6 digits").required("PIN code required"),
        bank_policy_accepted: Yup.boolean().oneOf([true], "You must accept the terms to proceed"),
    })
];

const LoanApplyForm = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    
    // ID to loan type mapping
    const loanTypeMapping = {
        '1': 'home_loan',
        '2': 'personal_loan',
        '3': 'loan_against_property',
        '4': 'business_loan',
        '5': 'car_loan',
        '6': 'education_loan'
    };

    // Get loan type from URL ID or slug
    const getLoanTypeFromUrl = () => {
        if (!slug) return "";
        // Check if slug is a number (ID)
        if (/^\d+$/.test(slug)) {
            return loanTypeMapping[slug] || "";
        }
        // Otherwise it's already a slug
        return slug;
    };

    const preselectedLoanType = getLoanTypeFromUrl();
    const isLoanTypePreselected = !!preselectedLoanType;
    
    const [step, setStep] = useState(0);
    const [loanSubServices, setLoanSubServices] = useState([]);
    const [loadingServices, setLoadingServices] = useState(true);
    const [submissionStatus, setSubmissionStatus] = useState(null); // null, 'success', 'error'
    const [submissionMessage, setSubmissionMessage] = useState('');
    const [verificationToken, setVerificationToken] = useState(null);
    const [isVerifying, setIsVerifying] = useState(false);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [submittedValues, setSubmittedValues] = useState(null);
    
    // Banks state
    const [banks, setBanks] = useState([]);
    const [loadingBanks, setLoadingBanks] = useState(false);
    const [bankSearchTerm, setBankSearchTerm] = useState('');
    const [showBankDropdown, setShowBankDropdown] = useState(false);
    const [selectedLoanType, setSelectedLoanType] = useState(preselectedLoanType || '');

    // Email domain suggestions
    const [showDomainSuggestions, setShowDomainSuggestions] = useState(false);
    const [localPart, setLocalPart] = useState("");
    const [filteredDomains, setFilteredDomains] = useState([]);
    const allowedDomains = [
        "gmail.com", "yahoo.com", "outlook.com", "hotmail.com",
        "icloud.com", "aol.com", "protonmail.com", "mail.com",
        "yandex.com", "rediffmail.com"
    ];

    const isLastStep = step === schemas.length - 1;

    const initialValues = {
        bank_id: "",
        gender: "",
        employee_type: "",
        loan_type: preselectedLoanType,
        full_name: "",
        contact_no: "",
        pan_no: "",
        request_amount: "",
        monthly_income: "",
        email: "",
        birth_date: "",
        state: "",
        city: "",
        pin_code: "",
        address: "",
        bank_policy_accepted: false,
        cibil_score: "",
        live_loan_emi: ""
    };

    useEffect(() => {
        const fetchServices = async () => {
            try {
                // Mocking the fetch for demonstration
                const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/sub-services`);
                const result = await response.json();
                const filteredLoans = result.data.filter(
                    item => item.service.slug.includes("loan") && item.status === "active"
                );
                setLoanSubServices(filteredLoans);
            } catch (error) {
                console.error("Error fetching sub-services:", error);
            } finally {
                setLoadingServices(false);
            }
        };
        fetchServices();
    }, []);

    // Fetch banks based on selected loan type
    const fetchBanks = async (loanType) => {
        if (!loanType) {
            setBanks([]);
            return;
        }

        setLoadingBanks(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/banks-list`);
            const result = await response.json();
            
            if (result.success && result.data) {
                // Filter banks based on loan type (sub_service_slug)
                const filteredBanks = result.data.filter(bank => 
                    bank.sub_service_slug && bank.sub_service_slug.includes(loanType)
                );
                setBanks(filteredBanks);
            } else {
                setBanks([]);
            }
        } catch (error) {
            console.error("Error fetching banks:", error);
            setBanks([]);
        } finally {
            setLoadingBanks(false);
        }
    };

    // Email domain suggestion handlers
    const handleEmailChange = (e, setFieldValue) => {
        const value = e.target.value;
        setFieldValue('email', value);

        const atIndex = value.indexOf("@");
        if (atIndex !== -1) {
            const beforeAt = value.substring(0, atIndex);
            const afterAt = value.substring(atIndex + 1);
            
            setLocalPart(beforeAt);
            
            const filtered = allowedDomains.filter(domain => 
                domain.toLowerCase().startsWith(afterAt.toLowerCase())
            );
            
            setFilteredDomains(filtered);
            setShowDomainSuggestions(filtered.length > 0 && afterAt.length > 0 && !filtered.some(d => d.toLowerCase() === afterAt.toLowerCase()));
        } else {
            setShowDomainSuggestions(false);
            setFilteredDomains([]);
        }
    };

    const handleDomainSelect = (domain, setFieldValue) => {
        setFieldValue('email', localPart + "@" + domain);
        setShowDomainSuggestions(false);
    };

    // Close bank dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if click is outside the bank dropdown container
            const bankDropdownContainer = event.target.closest('.bank-dropdown-container');
            const bankDropdownList = event.target.closest('.bank-dropdown-list');
            
            if (showBankDropdown && !bankDropdownContainer && !bankDropdownList) {
                setShowBankDropdown(false);
            }
        };

        if (showBankDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showBankDropdown]);

    // Fetch banks when selectedLoanType changes
    useEffect(() => {
        if (selectedLoanType) {
            fetchBanks(selectedLoanType);
        }
    }, [selectedLoanType]);

    // Fetch banks on initial load if loan type is preselected
    useEffect(() => {
        if (preselectedLoanType) {
            fetchBanks(preselectedLoanType);
        }
    }, []);

    // Convert number to words (Indian format)
    const numberToWords = (num) => {
        if (!num || num === 0) return "";
        
        const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
        const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
        const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
        
        const convertLessThanThousand = (n) => {
            if (n === 0) return '';
            if (n < 10) return ones[n];
            if (n < 20) return teens[n - 10];
            if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
            return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + convertLessThanThousand(n % 100) : '');
        };
        
        if (num < 1000) return convertLessThanThousand(num);
        if (num < 100000) {
            const thousands = Math.floor(num / 1000);
            const remainder = num % 1000;
            return convertLessThanThousand(thousands) + ' Thousand' + (remainder !== 0 ? ' ' + convertLessThanThousand(remainder) : '');
        }
        if (num < 10000000) {
            const lakhs = Math.floor(num / 100000);
            const remainder = num % 100000;
            return convertLessThanThousand(lakhs) + ' Lakh' + (remainder !== 0 ? ' ' + numberToWords(remainder) : '');
        }
        const crores = Math.floor(num / 10000000);
        const remainder = num % 10000000;
        return convertLessThanThousand(crores) + ' Crore' + (remainder !== 0 ? ' ' + numberToWords(remainder) : '');
    };

    // Calculate loan eligibility based on CIBIL, income, occupation, and existing EMI
    const calculateLoanEligibility = (values) => {
        if (!values.monthly_income) return null;

        const cibilScore = parseInt(values.cibil_score) || 0;
        const liveLoanEmi = parseInt(values.live_loan_emi) || 0;
        
        // Fixed interest rate for all banks (7% per annum)
        const interestRate = 7;
        const monthlyInterestRate = interestRate / 12 / 100;
        
        // Convert yearly income to monthly for business
        let monthlyIncome = parseInt(values.monthly_income);
        if (values.employee_type === "Business") {
            monthlyIncome = monthlyIncome / 12;
        }

        // CIBIL Score multiplier
        let cibilMultiplier = 1;
        if (cibilScore >= 750) {
            cibilMultiplier = 60; // Excellent - 60x monthly income
        } else if (cibilScore >= 700) {
            cibilMultiplier = 50; // Good - 50x monthly income
        } else if (cibilScore >= 650) {
            cibilMultiplier = 40; // Fair - 40x monthly income
        } else if (cibilScore >= 600) {
            cibilMultiplier = 30; // Poor - 30x monthly income
        } else if (!cibilScore || cibilScore === 0) {
            cibilMultiplier = 45; // Not provided - moderate multiplier
        } else {
            cibilMultiplier = 20; // Very poor - 20x monthly income
        }

        // Occupation type adjustment
        let occupationMultiplier = 1;
        if (values.employee_type === "Salaried") {
            occupationMultiplier = 1.1; // 10% bonus for salaried
        } else if (values.employee_type === "Self Employed") {
            occupationMultiplier = 1.0; // Standard
        } else if (values.employee_type === "Business") {
            occupationMultiplier = 0.9; // 10% reduction for business
        }

        // Calculate base eligibility
        let eligibleAmount = monthlyIncome * cibilMultiplier * occupationMultiplier;

        // Deduct existing EMI impact (EMI reduces eligibility)
        if (liveLoanEmi > 0) {
            // Reduce eligibility by 50x the EMI amount
            eligibleAmount = eligibleAmount - (liveLoanEmi * 50);
        }

        // Ensure minimum eligibility
        eligibleAmount = Math.max(eligibleAmount, 0);

        // Calculate EMI for different tenures (assuming eligible amount as loan)
        const calculateEMI = (principal, tenure) => {
            const n = tenure * 12; // months
            const emi = principal * monthlyInterestRate * Math.pow(1 + monthlyInterestRate, n) / (Math.pow(1 + monthlyInterestRate, n) - 1);
            return Math.round(emi);
        };

        return {
            amount: Math.round(eligibleAmount),
            interestRate: interestRate,
            emi5Years: calculateEMI(eligibleAmount, 5),
            emi10Years: calculateEMI(eligibleAmount, 10),
            emi15Years: calculateEMI(eligibleAmount, 15),
            emi20Years: calculateEMI(eligibleAmount, 20),
            cibilImpact: cibilScore >= 750 ? "Excellent" : cibilScore >= 700 ? "Good" : cibilScore >= 650 ? "Fair" : cibilScore >= 600 ? "Poor" : !cibilScore || cibilScore === 0 ? "Not Provided" : "Very Poor",
            hasLiveLoan: liveLoanEmi > 0,
            occupationType: values.employee_type
        };
    };

    const handleSubmit = async (values, actions) => {
        if (isLastStep) {
            const finalPayload = {
                bank_id: Number(values.bank_id),
                gender: values.gender,
                employee_type: values.employee_type,
                loan_type: values.loan_type,
                full_name: values.full_name,
                name: values.full_name, // Backend expects 'name' field for users table
                contact_no: values.contact_no,
                pan_no: values.pan_no.toUpperCase(),
                request_amount: Number(values.request_amount),
                monthly_income: Number(values.monthly_income),
                email: values.email,
                birth_date: values.birth_date,
                state: values.state,
                city: values.city,
                pin_code: values.pin_code,
                request_date: new Date().toISOString().split('T')[0],
                address: values.address,
                bank_policy_accepted: values.bank_policy_accepted,
                status: 'pending'
            };

            try {
                // Prepare headers
                const headers = {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                };

                // Add API key if available
                if (process.env.REACT_APP_API_KEY) {
                    headers['X-API-Key'] = process.env.REACT_APP_API_KEY;
                }

                console.log('Submitting loan application...');
                console.log('API URL:', `${process.env.REACT_APP_API_URL}/v1/leads`);
                console.log('Headers:', headers);
                console.log('Payload:', finalPayload);

                const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/leads`, {
                    method: 'POST',
                    headers: headers,
                    body: JSON.stringify(finalPayload)
                });

                console.log('Response status:', response.status);
                console.log('Response headers:', response.headers);
                
                const result = await response.json();
                console.log('Response data:', result);

                if (response.ok && result.success) {
                    setSubmissionStatus('success');
                    setSubmissionMessage(
                        result.message || 
                        'Application submitted successfully! Please check your email to verify your account.'
                    );
                    console.log('Loan Application Submitted:', result);
                    
                    // Log verification details
                    if (result.data && result.data.next_steps) {
                        console.log('Verification Token:', result.data.next_steps.token);
                        console.log('Verification expires in:', result.data.next_steps.verification_expires_in);
                    }

                    // Show success popup instead of auto-hide message
                    setSubmittedValues(values);
                    setShowSuccessPopup(true);
                } else {
                    // Handle validation errors from backend
                    if (result.errors && typeof result.errors === 'object') {
                        // Format validation errors for display
                        const errorMessages = Object.entries(result.errors)
                            .map(([field, messages]) => {
                                const fieldName = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                                const errorList = Array.isArray(messages) ? messages : [messages];
                                return `${fieldName}: ${errorList.join(', ')}`;
                            })
                            .join('\n');
                        
                        setSubmissionStatus('error');
                        setSubmissionMessage(errorMessages);
                        
                        // Also set Formik field errors
                        if (actions.setErrors) {
                            const formikErrors = {};
                            Object.entries(result.errors).forEach(([field, messages]) => {
                                const errorList = Array.isArray(messages) ? messages : [messages];
                                formikErrors[field] = errorList[0];
                            });
                            actions.setErrors(formikErrors);
                        }
                    } else {
                        setSubmissionStatus('error');
                        setSubmissionMessage(
                            result.message || 
                            `Failed to submit application. ${response.status === 401 ? 'Authentication required.' : 'Please try again.'}`
                        );
                    }
                    console.error('Submission Error:', result);

                    // Auto-hide error message after 8 seconds (longer for validation errors)
                    setTimeout(() => {
                        setSubmissionStatus(null);
                        setSubmissionMessage('');
                    }, 8000);
                }
            } catch (error) {
                console.error('Network Error Details:', error);
                console.error('Error name:', error.name);
                console.error('Error message:', error.message);
                setSubmissionStatus('error');
                setSubmissionMessage(`Network error: ${error.message}. Please check your connection and try again.`);
                
                // Auto-hide error message after 5 seconds
                setTimeout(() => {
                    setSubmissionStatus(null);
                    setSubmissionMessage('');
                }, 5000);
            } finally {
                actions.setSubmitting(false);
            }
        } else {
            setStep(step + 1);
            actions.setTouched({});
            actions.setSubmitting(false);
        }
    };

    const stepTitles = ["Personal Info", "Financial Info", "Address & Verify"];

    return (
        <div className="ln-app-wrapper">
            <div className="ln-app-card">
                <button className="close-modal-btn" onClick={() => navigate('/')}>
                    &times;
                </button>
                <div className="ln-app-header">
                    <h2>Apply for a Loan</h2>
                    <p>{stepTitles[step]}</p>
                </div>

                <div className="ln-app-stepper">
                    {[0, 1, 2].map((s) => (
                        <div key={s} className={`ln-step-item ${step >= s ? "active" : ""}`}>
                            <div className="ln-step-circle">{s + 1}</div>
                        </div>
                    ))}
                    <div className="ln-step-line" style={{ width: `${(step / 2) * 100}%` }}></div>
                </div>

                <Formik
                    initialValues={initialValues}
                    validationSchema={schemas[step]}
                    onSubmit={handleSubmit}
                    enableReinitialize={true}
                >
                    {({ isSubmitting, handleChange, errors, touched, setFieldValue, values }) => (
                        <Form className="ln-app-form">
                            {/* Global Error Message Display */}
                            {submissionStatus === 'error' && submissionMessage && (
                                <div style={{
                                    marginBottom: '20px',
                                    padding: '15px',
                                    background: '#FEE2E2',
                                    border: '2px solid #EF4444',
                                    borderRadius: '10px',
                                    color: '#991B1B'
                                }}>
                                    <div style={{display: 'flex', alignItems: 'flex-start', gap: '10px'}}>
                                        <i className="fas fa-exclamation-circle" style={{fontSize: '20px', marginTop: '2px'}}></i>
                                        <div style={{flex: 1}}>
                                            <strong style={{display: 'block', marginBottom: '5px'}}>Submission Error</strong>
                                            <div style={{whiteSpace: 'pre-line', fontSize: '14px'}}>{submissionMessage}</div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* STEP 0: Personal Information */}
                            {step === 0 && (
                                <div className="ln-app-step-content animation-fade">
                                    <div className="ln-app-group">
                                        <label className="ln-app-label">Full Name</label>
                                        <Field 
                                            name="full_name" 
                                            className={`ln-app-input ${errors.full_name && touched.full_name ? 'error' : ''}`}
                                            placeholder="Rajesh Sharma" 
                                        />
                                        <ErrorMessage name="full_name" component="div" className="ln-app-error" />
                                    </div>

                                    <div className="ln-app-row">
                                        <div className="ln-app-group">
                                            <label className="ln-app-label">Email</label>
                                            <Field 
                                                name="email" 
                                                type="email" 
                                                className={`ln-app-input ${errors.email && touched.email ? 'error' : ''}`}
                                                placeholder="example@mail.com"
                                                autoComplete="off"
                                                onChange={(e) => handleEmailChange(e, setFieldValue)}
                                            />
                                            {showDomainSuggestions && filteredDomains.length > 0 && (
                                                <ul className="domain-suggestions">
                                                    {filteredDomains.map((dom) => (
                                                        <li
                                                            key={dom}
                                                            onClick={() => handleDomainSelect(dom, setFieldValue)}
                                                        >
                                                            @{dom}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                            <ErrorMessage name="email" component="div" className="ln-app-error" />
                                        </div>
                                        <div className="ln-app-group">
                                            <label className="ln-app-label">Mobile Number</label>
                                            <Field 
                                                name="contact_no"
                                                type="tel"
                                                className={`ln-app-input ${errors.contact_no && touched.contact_no ? 'error' : ''}`}
                                                maxLength="10"
                                                placeholder="9737304440"
                                                onKeyPress={(e) => {
                                                    if (!/[0-9]/.test(e.key)) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                            />
                                            <ErrorMessage name="contact_no" component="div" className="ln-app-error" />
                                        </div>
                                    </div>

                                    <div className="ln-app-row">
                                        <div className="ln-app-group">
                                            <label className="ln-app-label">Date of Birth</label>
                                            <Field 
                                                name="birth_date" 
                                                type="date" 
                                                className={`ln-app-input ${errors.birth_date && touched.birth_date ? 'error' : ''}`}
                                                max={new Date().toISOString().split('T')[0]}
                                            />
                                            <ErrorMessage name="birth_date" component="div" className="ln-app-error" />
                                        </div>
                                        <div className="ln-app-group">
                                            <label className="ln-app-label">Gender</label>
                                            <Field 
                                                as="select" 
                                                name="gender" 
                                                className={`ln-app-select ${errors.gender && touched.gender ? 'error' : ''}`}
                                            >
                                                <option value="">Select...</option>
                                                <option value="male">Male</option>
                                                <option value="female">Female</option>
                                                <option value="other">Other</option>
                                            </Field>
                                            <ErrorMessage name="gender" component="div" className="ln-app-error" />
                                        </div>
                                    </div>

                                    <div className="ln-app-group">
                                        <label className="ln-app-label">PAN Number</label>
                                        <Field 
                                            name="pan_no"
                                            className={`ln-app-input uppercase-text ${errors.pan_no && touched.pan_no ? 'error' : ''}`}
                                            placeholder="AVAOD1239S"
                                            maxLength="10"
                                            onKeyPress={(e) => {
                                                const value = e.target.value;
                                                const key = e.key;
                                                // First 5 characters: only letters
                                                if (value.length < 5 && !/[a-zA-Z]/.test(key)) {
                                                    e.preventDefault();
                                                }
                                                // Characters 6-9: only numbers
                                                else if (value.length >= 5 && value.length < 9 && !/[0-9]/.test(key)) {
                                                    e.preventDefault();
                                                }
                                                // 10th character: only letter
                                                else if (value.length === 9 && !/[a-zA-Z]/.test(key)) {
                                                    e.preventDefault();
                                                }
                                            }}
                                            onInput={(e) => {
                                                e.target.value = e.target.value.toUpperCase();
                                            }}
                                        />
                                        <ErrorMessage name="pan_no" component="div" className="ln-app-error" />
                                    </div>
                                </div>
                            )}

                            {/* STEP 1: Employment & Loan Details */}
                            {step === 1 && (
                                <div className="ln-app-step-content animation-fade">
                                    <div className="ln-app-row">
                                        <div className="ln-app-group">
                                            <label className="ln-app-label">Loan Product</label>
                                            <Field 
                                                as="select" 
                                                name="loan_type" 
                                                className={`ln-app-select ${errors.loan_type && touched.loan_type ? 'error' : ''}`}
                                                disabled={isLoanTypePreselected}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setFieldValue('loan_type', value);
                                                    setSelectedLoanType(value);
                                                    // Reset bank selection when loan type changes
                                                    setFieldValue('bank_id', '');
                                                    setBankSearchTerm('');
                                                }}
                                            >
                                                <option value="">{loadingServices ? "Loading..." : "Select Loan"}</option>
                                                <option value="home_loan">Home Loan</option>
                                                <option value="personal_loan">Personal Loan</option>
                                                <option value="loan_against_property">Loan Against Property</option>
                                                <option value="business_loan">Business Loan</option>
                                                <option value="car_loan">Car Loan</option>
                                                <option value="education_loan">Education Loan</option>
                                                {loanSubServices.map(loan => (
                                                    <option key={loan.id} value={loan.slug}>{loan.name}</option>
                                                ))}
                                            </Field>
                                            <ErrorMessage name="loan_type" component="div" className="ln-app-error" />
                                        </div>
                                        <div className="ln-app-group">
                                            <label className="ln-app-label">Preferred Bank</label>
                                            <div className="bank-dropdown-container" style={{position: 'relative'}}>
                                                <input
                                                    type="text"
                                                    className={`ln-app-input ${errors.bank_id && touched.bank_id ? 'error' : ''}`}
                                                    placeholder={loadingBanks ? "Loading banks..." : "Search bank..."}
                                                    value={bankSearchTerm}
                                                    onChange={(e) => {
                                                        setBankSearchTerm(e.target.value);
                                                        setShowBankDropdown(true);
                                                    }}
                                                    onFocus={() => setShowBankDropdown(true)}
                                                    disabled={!values.loan_type || loadingBanks}
                                                    autoComplete="off"
                                                />
                                                {showBankDropdown && banks.length > 0 && (
                                                    <ul className="bank-dropdown-list">
                                                        {banks
                                                            .filter(bank => {
                                                                // If search is empty, show all banks
                                                                if (!bankSearchTerm.trim()) {
                                                                    return true;
                                                                }
                                                                
                                                                const searchLower = bankSearchTerm.toLowerCase();
                                                                const bankName = (bank.bank_name || '').toLowerCase();
                                                                const shortName = (bank.bank_short_name || '').toLowerCase();
                                                                
                                                                // Create display text to match against
                                                                const displayText = bank.bank_short_name 
                                                                    ? `${bank.bank_name} (${bank.bank_short_name})`.toLowerCase()
                                                                    : bankName;
                                                                
                                                                // Search in bank name, short name, or full display text
                                                                return bankName.includes(searchLower) || 
                                                                       shortName.includes(searchLower) || 
                                                                       displayText.includes(searchLower);
                                                            })
                                                            .slice(0, 10)
                                                            .map(bank => {
                                                                // Display format: "Bank Name (SHORT)"
                                                                const displayText = bank.bank_short_name 
                                                                    ? `${bank.bank_name} (${bank.bank_short_name})`
                                                                    : bank.bank_name;
                                                                
                                                                return (
                                                                    <li
                                                                        key={bank.id}
                                                                        onClick={() => {
                                                                            setFieldValue('bank_id', bank.id);
                                                                            setBankSearchTerm(displayText);
                                                                            setShowBankDropdown(false);
                                                                        }}
                                                                        className="bank-dropdown-item"
                                                                    >
                                                                        {displayText}
                                                                    </li>
                                                                );
                                                            })
                                                        }
                                                        {banks.filter(bank => {
                                                            if (!bankSearchTerm.trim()) {
                                                                return true;
                                                            }
                                                            const searchLower = bankSearchTerm.toLowerCase();
                                                            const bankName = (bank.bank_name || '').toLowerCase();
                                                            const shortName = (bank.bank_short_name || '').toLowerCase();
                                                            const displayText = bank.bank_short_name 
                                                                ? `${bank.bank_name} (${bank.bank_short_name})`.toLowerCase()
                                                                : bankName;
                                                            return bankName.includes(searchLower) || 
                                                                   shortName.includes(searchLower) || 
                                                                   displayText.includes(searchLower);
                                                        }).length === 0 && (
                                                            <li className="bank-dropdown-item" style={{color: '#64748b', fontStyle: 'italic'}}>
                                                                No banks found
                                                            </li>
                                                        )}
                                                    </ul>
                                                )}
                                                {!values.loan_type && (
                                                    <div style={{marginTop: '5px', fontSize: '12px', color: '#64748b'}}>
                                                        Please select a loan product first
                                                    </div>
                                                )}
                                            </div>
                                            <ErrorMessage name="bank_id" component="div" className="ln-app-error" />
                                        </div>
                                    </div>

                                    <div className="ln-app-group">
                                        <label className="ln-app-label">Occupation Type</label>
                                        <Field 
                                            as="select" 
                                            name="employee_type" 
                                            className={`ln-app-select ${errors.employee_type && touched.employee_type ? 'error' : ''}`}
                                        >
                                            <option value="">Select...</option>
                                            <option value="Salaried">Salaried</option>
                                            <option value="Self Employed">Self Employed</option>
                                            <option value="Business">Business</option>
                                        </Field>
                                        <ErrorMessage name="employee_type" component="div" className="ln-app-error" />
                                    </div>

                                    <div className="ln-app-row">
                                        <div className="ln-app-group">
                                            <label className="ln-app-label">
                                                {values.employee_type === "Business" ? "Yearly Income (₹)" : "Monthly Income (₹)"}
                                            </label>
                                            <Field 
                                                name="monthly_income" 
                                                type="number" 
                                                className={`ln-app-input ${errors.monthly_income && touched.monthly_income ? 'error' : ''}`}
                                                placeholder={values.employee_type === "Business" ? "900000" : "75000"}
                                            />
                                            {values.monthly_income && (
                                                <div style={{marginTop: '5px', fontSize: '12px', color: '#4F46E5', fontWeight: '500'}}>
                                                    {numberToWords(parseInt(values.monthly_income))} Rupees
                                                </div>
                                            )}
                                            <ErrorMessage name="monthly_income" component="div" className="ln-app-error" />
                                        </div>
                                        <div className="ln-app-group">
                                            <label className="ln-app-label">Request Amount (₹)</label>
                                            <Field 
                                                name="request_amount" 
                                                type="number" 
                                                className={`ln-app-input ${errors.request_amount && touched.request_amount ? 'error' : ''}`}
                                                placeholder="500000" 
                                            />
                                            {values.request_amount && (
                                                <div style={{marginTop: '5px', fontSize: '12px', color: '#4F46E5', fontWeight: '500'}}>
                                                    {numberToWords(parseInt(values.request_amount))} Rupees
                                                </div>
                                            )}
                                            <ErrorMessage name="request_amount" component="div" className="ln-app-error" />
                                        </div>
                                    </div>

                                    <div className="ln-app-row">
                                        <div className="ln-app-group">
                                            <label className="ln-app-label">
                                                CIBIL Score
                                                <a 
                                                    href="/cibil" 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        marginLeft: '10px',
                                                        color: '#4F46E5',
                                                        fontSize: '13px',
                                                        textDecoration: 'none',
                                                        fontWeight: '500',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}
                                                >
                                                    <i className="fas fa-external-link-alt" style={{fontSize: '11px'}}></i>
                                                    Check CIBIL
                                                </a>
                                            </label>
                                            <Field 
                                                name="cibil_score" 
                                                type="number" 
                                                className={`ln-app-input ${errors.cibil_score && touched.cibil_score ? 'error' : ''}`}
                                                placeholder="750"
                                                min="300"
                                                max="900"
                                                onInput={(e) => {
                                                    // Restrict to 3 digits only
                                                    if (e.target.value.length > 3) {
                                                        e.target.value = e.target.value.slice(0, 3);
                                                    }
                                                }}
                                            />
                                            <ErrorMessage name="cibil_score" component="div" className="ln-app-error" />
                                        </div>
                                        <div className="ln-app-group">
                                            <label className="ln-app-label">Live Loan EMI Amount (₹)</label>
                                            <Field 
                                                name="live_loan_emi" 
                                                type="number" 
                                                className={`ln-app-input ${errors.live_loan_emi && touched.live_loan_emi ? 'error' : ''}`}
                                                placeholder="15000" 
                                            />
                                                <ErrorMessage name="live_loan_emi" component="div" className="ln-app-error" />
                                            </div>
                                        </div>
                                </div>
                            )}

                            {/* STEP 2: Address & Consent */}
                            {step === 2 && (
                                <div className="ln-app-step-content animation-fade">

                                    <div className="ln-app-group">
                                        <label className="ln-app-label">Full Address</label>
                                        <Field 
                                            as="textarea" 
                                            name="address" 
                                            className={`ln-app-input ${errors.address && touched.address ? 'error' : ''}`}
                                            rows="3" 
                                            placeholder="123, ABC Apartments, Andheri East..." 
                                        />
                                        <ErrorMessage name="address" component="div" className="ln-app-error" />
                                    </div>

                                    <div className="ln-app-row">
                                        <div className="ln-app-group">
                                            <label className="ln-app-label">State</label>
                                            <Field 
                                                name="state" 
                                                className={`ln-app-input ${errors.state && touched.state ? 'error' : ''}`}
                                                placeholder="Maharashtra" 
                                            />
                                            <ErrorMessage name="state" component="div" className="ln-app-error" />
                                        </div>
                                        <div className="ln-app-group">
                                            <label className="ln-app-label">City</label>
                                            <Field 
                                                name="city" 
                                                className={`ln-app-input ${errors.city && touched.city ? 'error' : ''}`}
                                                placeholder="Mumbai" 
                                            />
                                            <ErrorMessage name="city" component="div" className="ln-app-error" />
                                        </div>
                                    </div>

                                    <div className="ln-app-group">
                                        <label className="ln-app-label">PIN Code</label>
                                        <Field 
                                            name="pin_code"
                                            type="tel"
                                            className={`ln-app-input ${errors.pin_code && touched.pin_code ? 'error' : ''}`}
                                            maxLength="6"
                                            placeholder="400001"
                                            onKeyPress={(e) => {
                                                if (!/[0-9]/.test(e.key)) {
                                                    e.preventDefault();
                                                }
                                            }}
                                        />
                                        <ErrorMessage name="pin_code" component="div" className="ln-app-error" />
                                    </div>

                                    <div className="ln-app-consent-box">
                                        <Field type="checkbox" name="bank_policy_accepted" id="bank_policy_accepted" className="ln-checkbox" />
                                        <label htmlFor="bank_policy_accepted" className="ln-consent-label">
                                            I agree to receive loan updates and promotional from <strong>Krishana Finconcept</strong> via WhatsApp and other accept the <a href="/privacy-security-policy" target="_blank" rel="noopener noreferrer">Terms & Conditions</a>
                                        </label>
                                    </div>
                                    <ErrorMessage name="bank_policy_accepted" component="div" className="ln-app-error" />
                                </div>
                            )}

                            

                            <div className="ln-app-actions">
                                {step > 0 ? (
                                    <button type="button" className="ln-btn-secondary" onClick={() => setStep(step - 1)}>
                                        Go Back
                                    </button>
                                ) : (
                                    <div className="ln-spacer"></div>
                                )}
                                <button type="submit" className="ln-btn-primary" disabled={isSubmitting}>
                                    {isSubmitting ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i> Processing...
                                        </>
                                    ) : (
                                        isLastStep ? "Submit Application" : "Next"
                                    )}
                                </button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>

            {/* Success Popup Modal */}
            {showSuccessPopup && submittedValues && (
                <div className="ln-popup-overlay">
                    <div className="ln-popup-modal" style={{maxWidth: '600px'}}>
                        <div className="ln-popup-icon">
                            <i className="fas fa-check-circle"></i>
                        </div>
                        <h2 className="ln-popup-title">Application Submitted</h2>
                        
                        {/* Email Verification Box First */}
                        <div style={{
                            background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
                            border: '2px solid #3B82F6',
                            borderRadius: '12px',
                            padding: '20px',
                            marginBottom: '20px',
                            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.15)'
                        }}>
                            <div style={{display: 'flex', alignItems: 'flex-start', gap: '15px'}}>
                                <div style={{
                                    background: '#3B82F6',
                                    borderRadius: '50%',
                                    width: '48px',
                                    height: '48px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}>
                                    <i className="fas fa-envelope" style={{fontSize: '22px', color: 'white'}}></i>
                                </div>
                                <div style={{textAlign: 'left', flex: 1}}>
                                    <h3 style={{margin: '0 0 8px 0', fontSize: '16px', color: '#1E40AF', fontWeight: '700'}}>
                                        Verification Email Sent
                                    </h3>
                                    <p style={{margin: 0, fontSize: '14px', color: '#1E3A8A', lineHeight: '1.6'}}>
                                        Please check your <strong>email inbox</strong> for the verification link to complete and activate your loan application.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Loan Eligibility Display Second */}
                        {(() => {
                            const eligibility = calculateLoanEligibility(submittedValues);
                            
                            if (eligibility) {
                                return (
                                    <div style={{
                                        marginBottom: '25px',
                                        padding: '20px',
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        borderRadius: '12px',
                                        color: 'white',
                                        textAlign: 'left'
                                    }}>
                                        <h3 style={{margin: '0 0 15px 0', fontSize: '16px', fontWeight: '600', textAlign: 'center'}}>
                                            <i className="fas fa-calculator" style={{marginRight: '8px'}}></i>
                                            Your Estimated Loan Eligibility
                                        </h3>
                                        <div style={{fontSize: '28px', fontWeight: '700', marginBottom: '8px', textAlign: 'center'}}>
                                            ₹ {eligibility.amount.toLocaleString('en-IN')}
                                        </div>
                                        <div style={{fontSize: '13px', opacity: '0.9', marginBottom: '15px', textAlign: 'center', fontStyle: 'italic'}}>
                                            {numberToWords(eligibility.amount)} Rupees
                                        </div>

                                        <div style={{fontSize: '13px', opacity: '0.95', lineHeight: '1.6', marginBottom: '15px'}}>
                                            <div style={{marginBottom: '8px'}}>
                                                <i className="fas fa-chart-line" style={{marginRight: '8px', width: '16px'}}></i>
                                                <strong>CIBIL Impact:</strong> {eligibility.cibilImpact}
                                            </div>
                                            <div style={{marginBottom: '8px'}}>
                                                <i className="fas fa-briefcase" style={{marginRight: '8px', width: '16px'}}></i>
                                                <strong>Occupation:</strong> {eligibility.occupationType}
                                            </div>
                                            {eligibility.hasLiveLoan && (
                                                <div style={{marginBottom: '8px'}}>
                                                    <i className="fas fa-exclamation-circle" style={{marginRight: '8px', width: '16px'}}></i>
                                                    <strong>Note:</strong> Existing EMI reduces eligibility
                                                </div>
                                            )}
                                        </div>
                                        <div style={{
                                            marginTop: '15px',
                                            padding: '12px',
                                            background: '#FEF3C7',
                                            borderRadius: '8px',
                                            fontSize: '12px',
                                            color: '#92400E',
                                            fontWeight: '600',
                                            border: '2px solid #F59E0B',
                                            textAlign: 'center'
                                        }}>
                                            <i className="fas fa-info-circle" style={{marginRight: '6px'}}></i>
                                            This is an estimated amount. Final approval depends on bank verification.
                                        </div>
                                    </div>
                                );
                            }
                            return null;
                        })()}

                        <button 
                            className="ln-popup-btn"
                            onClick={() => {
                                setShowSuccessPopup(false);
                                navigate('/');
                            }}
                        >
                            <i className="fas fa-check" style={{marginRight: '8px'}}></i>
                            OK, Got It!
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LoanApplyForm;